import type { ClubEventRow } from "@/api/club/ClubApi";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import React, { useMemo } from "react";
import { formatMonthYearDE } from "@/utils/isoDate";
import { Pressable, StyleSheet, Text, View } from "react-native";

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dayKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Expandiert Events auf alle betroffenen Kalendertage (lokal). */
export function expandEventsToDays(
  events: ClubEventRow[]
): Map<string, ClubEventRow[]> {
  const map = new Map<string, ClubEventRow[]>();
  for (const ev of events) {
    const start = new Date(ev.starts_at);
    const end = ev.ends_at ? new Date(ev.ends_at) : start;
    const cursor = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate()
    );
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cursor.getTime() <= last.getTime()) {
      const k = dayKey(cursor);
      const list = map.get(k) ?? [];
      list.push(ev);
      map.set(k, list);
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return map;
}

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export function CalendarMonth({
  month,
  events,
  onSelectDay,
  onSelectEvent,
  onChangeMonth,
}: {
  month: Date;
  events: ClubEventRow[];
  onSelectDay?: (day: Date) => void;
  onSelectEvent?: (event: ClubEventRow) => void;
  onChangeMonth?: (next: Date) => void;
}) {
  const { theme } = useTheme();
  const s = useStyles();
  const byDay = useMemo(() => expandEventsToDays(events), [events]);
  const first = startOfMonth(month);
  // Monday-first: JS getDay() Sun=0 → shift
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0
  ).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();
  const title = formatMonthYearDE(month.getFullYear(), month.getMonth());

  return (
    <View style={{ gap: theme.space.sm }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingHorizontal: theme.space.xs,
        }}
      >
        <Pressable
          onPress={() => onChangeMonth?.(addMonths(month, -1))}
          accessibilityLabel="Vorheriger Monat"
          hitSlop={8}
          style={{ padding: theme.space.sm }}
        >
          <Text style={[s.textBody, { color: theme.color.primary, fontWeight: "700" }]}>
            ‹
          </Text>
        </Pressable>
        <Text
          style={[
            s.textH3,
            {
              textTransform: "capitalize",
              color: theme.color.text,
            },
          ]}
        >
          {title}
        </Text>
        <Pressable
          onPress={() => onChangeMonth?.(addMonths(month, 1))}
          accessibilityLabel="Nächster Monat"
          hitSlop={8}
          style={{ padding: theme.space.sm }}
        >
          <Text style={[s.textBody, { color: theme.color.primary, fontWeight: "700" }]}>
            ›
          </Text>
        </Pressable>
      </View>

      <View
        style={{
          flexDirection: "row",
          paddingBottom: theme.space.xs,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: theme.color.border,
        }}
      >
        {WEEKDAYS.map((w) => (
          <View key={w} style={{ flex: 1, alignItems: "center" }}>
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.tiny,
                fontWeight: "700",
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              {w}
            </Text>
          </View>
        ))}
      </View>

      <View
        style={{
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: theme.color.border,
          borderRadius: theme.radius.md,
          overflow: "hidden",
          backgroundColor: theme.color.surface,
        }}
      >
        {Array.from({ length: cells.length / 7 }, (_, row) => (
          <View key={`r-${row}`} style={{ flexDirection: "row" }}>
            {cells.slice(row * 7, row * 7 + 7).map((day, col) => {
              const idx = row * 7 + col;
              if (!day) {
                return (
                  <View
                    key={`e-${idx}`}
                    style={{ flex: 1, minHeight: 52 }}
                  />
                );
              }
              const list = byDay.get(dayKey(day)) ?? [];
              const isToday = sameDay(day, today);
              return (
                <Pressable
                  key={dayKey(day)}
                  onPress={() => {
                    onSelectDay?.(day);
                    if (list[0]) onSelectEvent?.(list[0]);
                  }}
                  style={{
                    flex: 1,
                    minHeight: 52,
                    padding: theme.space.xs,
                    borderWidth: StyleSheet.hairlineWidth,
                    borderColor: theme.color.border,
                    backgroundColor: isToday
                      ? theme.color.accentBg
                      : theme.color.surface,
                  }}
                >
                  <Text
                    style={{
                      fontSize: theme.fontSize.tiny,
                      fontWeight: isToday ? "700" : "500",
                      color: isToday
                        ? theme.color.primary
                        : theme.color.text,
                    }}
                  >
                    {day.getDate()}
                  </Text>
                  {list.slice(0, 2).map((ev) => (
                    <Text
                      key={ev.item_id}
                      numberOfLines={1}
                      style={{
                        color: ev.cancelled_at
                          ? theme.color.danger
                          : theme.color.textSecondary,
                        textDecorationLine: ev.cancelled_at
                          ? "line-through"
                          : "none",
                        fontSize: theme.fontSize.tiny,
                        fontWeight: "500",
                      }}
                    >
                      {ev.title}
                    </Text>
                  ))}
                  {list.length > 2 ? (
                    <Text
                      style={{
                        color: theme.color.muted,
                        fontSize: theme.fontSize.tiny,
                      }}
                    >
                      +{list.length - 2}
                    </Text>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
}
